import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import startOfToday from 'date-fns/startOfToday';
import { DateString, FromTo } from '@shared/core/types';
import { FieldType, FormField } from '@shared/shared/form-fields';
import { FormFieldsChangesSubService, FormFieldsInstanceService } from '@shared/shared';
import { getVacationStatus } from '@crm/shared/persons/helpers/vacation';
import {
  EducationService,
  EducationServiceVacation,
  EducationServiceVacationCreateRequest,
  EducationServiceVacationUpdateRequest,
  VacationStatus,
  VacationType,
} from '@crm/shared/persons/types';
import { registerTranslationsManually } from '@skyeng/libs/shared/i18n';
import { TranslocoService } from '@ngneat/transloco';
import { VacationManagementApiService } from '@crm/shared/persons/services/vacation-management-api.service';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';

@Component({
  selector: 'crm-shared-persons-vacation',
  templateUrl: './vacation.component.html',
  styleUrls: ['./vacation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormFieldsInstanceService, FormFieldsChangesSubService],
})
@DecorateUntilDestroy()
export class VacationComponent implements OnInit, OnChanges {
  @Input() currentOrFutureVacation: EducationServiceVacation | null;
  @Input() educationService: EducationService;

  @Output() sendOnVacation = new EventEmitter<EducationServiceVacationCreateRequest>();
  @Output() changeVacationDates = new EventEmitter<EducationServiceVacationUpdateRequest>();
  @Output() cancelVacation = new EventEmitter<void>();
  @Output() returnFromVacation = new EventEmitter<void>();

  form: UntypedFormGroup;
  datesField: FormField;
  reason: FormField;
  vacationStatus: VacationStatus = null;
  VacationStatus = VacationStatus;

  constructor(
    private formFieldsService: FormFieldsInstanceService,
    private formFieldsSub: FormFieldsChangesSubService,
    private transloco: TranslocoService,
    private vacationManagementApi: VacationManagementApiService,
    private ref: ChangeDetectorRef,
  ) {
    registerTranslationsManually('personsVacation', this.transloco, import.meta.webpackContext!('./translation'));
  }

  ngOnInit(): void {
    this.setFields();
    this.setVacationStatus();
    this.form = this.formFieldsService.getForm();
    this.fillForm();

    this.form.get('dates').setValidators([Validators.required, this.vacationDurationValidator]);
    this.form.get('reason').setValidators([Validators.required]);
    this.form.updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.currentOrFutureVacation.isFirstChange()) {
      this.setVacationStatus();
      this.fillForm();
    }
  }

  private setFields(): void {
    this.datesField = {
      key: 'dates',
      type: FieldType.dateInterval,
      name: this.transloco.translate('personsVacation.period'),
      validators: {
        // DO NOT REMOVE! Although we define custom validators below and this one is lost,
        // it is still required, because it sets [minDate] internally for date picker.
        min: startOfToday(),
      },
    };

    this.reason = {
      key: 'reason',
      type: FieldType.radioButton,
      name: this.transloco.translate('personsVacation.reasonTitle'),
      value: null,
      options: [],
    };

    this.vacationManagementApi
      .getReasonsByStk(this.educationService.serviceTypeKey)
      .pipe(takeUntilDestroyed(this))
      .subscribe(reasons => {
        const resultReasons = reasons.map(reason => {
          return { label: this.transloco.translate('personsVacation.reasons.' + reason), value: reason };
        });

        if (0 !== resultReasons.length) {
          this.reason = {
            ...this.reason,
            options: resultReasons,
            value: resultReasons[0].value,
          };

          this.ref.detectChanges();
        }
      });
  }

  send(): void {
    const values = this.formFieldsService.serializeValues();
    const dates = values.dates as FromTo<DateString>;
    if (!dates) {
      return;
    }

    this.sendOnVacation.emit({
      type: values.schoolBreak ? VacationType.schoolBreak : VacationType.vacation,
      startsAt: dates.from,
      endsAt: dates.to,
      reason: values.reason.toString(),
    });
  }

  change(): void {
    const dates = this.formFieldsService.serializeValues().dates as FromTo<DateString>;
    if (!dates) {
      return;
    }

    this.changeVacationDates.emit({
      startsAt: dates.from,
      endsAt: dates.to,
    });
  }

  cancel(): void {
    this.cancelVacation.emit();
  }

  return(): void {
    this.returnFromVacation.emit();
  }

  private fillForm(): void {
    this.datesField = {
      ...this.datesField,
      value: null,
      disabled: !this.vacationStatus,
    };

    if (null !== this.currentOrFutureVacation) {
      this.datesField = {
        ...this.datesField,
        value: {
          from: this.currentOrFutureVacation.startsAt,
          to: this.currentOrFutureVacation.endsAt,
        },
      };
    }

    this.formFieldsSub.pauseAllSubscriptions();
    this.formFieldsService.setFields([this.datesField, this.reason]);
    this.formFieldsSub.resubscribeAll();
  }

  private setVacationStatus(): void {
    this.vacationStatus = getVacationStatus(this.currentOrFutureVacation);
  }

  private vacationDurationValidator(control: AbstractControl): ValidationErrors | null {
    if (null === control.value) {
      return null;
    }

    const from = control.value[0];
    const to = control.value[1];
    if (Math.abs(differenceInCalendarDays(from, to)) > 180) {
      return { vacationDuration: { value: control.value } };
    }

    return null;
  }
}
